import type { TimeframeStore } from "../../data/DataStore";

// Faithful port of the "DSzone" Pine v5 indicator (v2.15 by default; v2.13 with { v214: false, sw: false, minBoringBody: 0 }).
// Rules: A,B1-3,C,D,F/G/O,I,J,R,S,T,V,M,X,Y,Z,Q,U,L1(anyLegoutTRok),LO(legoutStair),
// LD(leg-in→leg-out distance),K(big leg-out needs a real body),LW(max wicky leg-outs).
// W / E / L are QUALITY STARS (informational), not gates.
// minLegOut = maxLegOut = 3 (detects 3-leg-out zones only, per the script defaults).

export interface ZoneType {
  name: "DBR" | "RBR" | "RBD" | "DBD";
  n: number; // leg-out count
  b: number; // boring count
  w: boolean; // star: clean boring wicks
  e: boolean; // star: leg-in close wick
}

export interface ZoneResult {
  span: number;
  color: string;
  types: ZoneType[];
}

export interface EntryEvent {
  bar: number; // candle that touched (or nearly touched) the entry
  demand: boolean;
  text: "✓ entry" | "✗ fast" | "✗ straight";
}

// entry filters (Pine v2.15 defaults)
const FAST_BARS = 4;
const NEAR_PCT = 10;
const MAX_WAIT = 500;

// tunables (Pine v2.13 input defaults)
const ATB_LEN = 100;
const ATB_PCT = 75;
const dBodyMul = 1.0;
const tMaxRatio = 6.0;
const maxLegOut = 3;
const minLegOut = 3;
const maxBoring = 3;
const vBodyPct = 50.0;
const wickPct = 1.0;
const xPct = 25.0;
const legBigBodyPct = 30.0;
const maxWickyLegouts = 1;

// bgcolor tints (color.new(color.X, 75) => ~0.14 alpha in-app)
const COL_DBR = "rgba(0,255,0,0.14)";
const COL_RBR = "rgba(0,255,255,0.14)";
const COL_RBD = "rgba(255,0,0,0.14)";
const COL_DBD = "rgba(255,165,0,0.14)";

// label backgrounds (color.new(X,20))
export const LABEL_COLORS: Record<string, string> = {
  DBR: "rgba(0,128,0,0.9)",
  RBR: "rgba(30,90,220,0.9)",
  RBD: "rgba(220,40,40,0.9)",
  DBD: "rgba(230,140,0,0.9)",
};

// Rule J — timeframe-based leg-out multiplier.
function computeLegoutMul(tfSeconds: number): number {
  const curMin = tfSeconds / 60;
  const slots = [
    { on: true, tf: 5, val: 1.25 },
    { on: true, tf: 120, val: 1.5 },
    { on: false, tf: 240, val: 1.5 },
    { on: false, tf: 1440, val: 1.75 },
    { on: false, tf: 10080, val: 2.0 },
  ];
  const fallback = 1.5;
  let bestMin = Infinity;
  let jMatched = fallback;
  let maxMin = 0;
  let jLargest = fallback;
  for (const s of slots) {
    const g = s.on ? s.tf : 0;
    if (g > maxMin) {
      maxMin = g;
      jLargest = s.val;
    }
    const e = s.on && s.tf >= curMin ? s.tf : Infinity;
    if (e < bestMin) {
      bestMin = e;
      jMatched = s.val;
    }
  }
  return bestMin < Infinity ? jMatched : maxMin > 0 ? jLargest : fallback;
}

export class DSZoneEngine {
  private d: TimeframeStore;
  private legoutMul: number;
  private atbCache = new Map<number, number>();
  private resCache = new Map<number, ZoneResult | null>();
  private evCache = new Map<number, EntryEvent | null>();
  readonly maxSpan = maxLegOut + maxBoring + 1;

  // v214: opt-in rules from pine/DSzone_v2.14.pine — LT (leg-out reach), FL (RBR/DBD 1st leg-out closes beyond
  // the boring block), LB (no leg-out beyond the boring distal), NB (every boring body ≤ x pts, per timeframe),
  // plus the v2.13-Pine extra clauses in B1 and L1. App default = Pine v2.15: v214 + SW + MB 0.75, BC off
  // (scripts pass every option explicitly, so their versions are unchanged).
  private v214: boolean;
  private sw: boolean;
  private bc: boolean;
  private minLO: number;
  private maxB: number;
  private minBB: number;
  private nbMax: number;

  constructor(store: TimeframeStore, tfSeconds: number, opts: { v214?: boolean; sw?: boolean; bc?: boolean; minLegOut?: number; maxBoring?: number; minBoringBody?: number } = {}) {
    this.d = store;
    this.legoutMul = computeLegoutMul(tfSeconds);
    this.v214 = opts.v214 ?? true;
    this.sw = opts.sw ?? true;
    this.bc = !!opts.bc;   // Rule BC: boring count > leg-out count → reject
    this.minLO = opts.minLegOut ?? minLegOut;
    this.maxB = opts.maxBoring ?? maxBoring;
    this.minBB = opts.minBoringBody ?? 0.75;   // Rule MB: every boring candle's body must be ≥ this many points (0 = off)
    this.nbMax = ({ 5: 0.75, 30: 0.5 } as Record<number, number>)[tfSeconds] ?? -1;
  }

  // 75th-percentile of |close-open| over ATB_LEN bars ending at absIdx.
  private atbAt(absIdx: number): number {
    const cached = this.atbCache.get(absIdx);
    if (cached !== undefined) return cached;
    const d = this.d;
    const bodies: number[] = [];
    for (let j = Math.max(0, absIdx - (ATB_LEN - 1)); j <= absIdx; j++) {
      const o = d.openAt(j);
      const c = d.closeAt(j);
      if (isFinite(o) && isFinite(c)) bodies.push(Math.abs(c - o));
    }
    let out: number;
    if (bodies.length === 0) out = Infinity;
    else {
      bodies.sort((a, b) => a - b);
      const idx = Math.floor((ATB_PCT / 100) * (bodies.length - 1));
      out = bodies[idx];
    }
    if (this.atbCache.size > 300000) this.atbCache.clear();
    this.atbCache.set(absIdx, out);
    return out;
  }

  // Evaluate one (n,b,ptype) at signal bar k. offset i => bar k-i.
  private evalPattern(k: number, n: number, b: number, ptype: string): { ok: boolean; w: boolean; e: boolean } | null {
    const d = this.d;
    const i1 = n + b;
    const ib = n + b + 1;
    if (k - ib < 0) return null;

    const H = (i: number) => d.highAt(k - i);
    const L = (i: number) => d.lowAt(k - i);
    const O = (i: number) => d.openAt(k - i);
    const C = (i: number) => d.closeAt(k - i);
    const ATR = (i: number) => d.atrAt(k - i);
    const TR = (i: number) => d.trAt(k - i);
    const ATB = (i: number) => this.atbAt(k - i);

    const i2 = n;
    const i2e = n + b - 1;

    const r1 = H(i1) - L(i1);
    const bd1 = Math.abs(C(i1) - O(i1));
    if (!isFinite(r1) || !isFinite(ATR(i1))) return { ok: false, w: false, e: false };

    // Rule E (star)
    const upW1 = H(i1) - Math.max(O(i1), C(i1));
    const dnW1 = Math.min(O(i1), C(i1)) - L(i1);
    const e_closeOk = r1 > 0 && (C(i1) < O(i1) ? dnW1 >= (wickPct / 100) * r1 : upW1 >= (wickPct / 100) * r1);
    const e_openSmall = r1 > 0 && (C(i1) < O(i1) ? upW1 <= 0.1 * r1 : dnW1 <= 0.1 * r1);
    const e_ok = e_closeOk && e_openSmall;

    // Rule L1: at least one leg-out range > its ATR
    let anyLegoutTRok = false;
    for (let kk = 0; kk <= n - 1; kk++) {
      if (H(kk) - L(kk) > ATR(kk)) anyLegoutTRok = true;
      if (this.v214 && H(kk) - L(kk) >= 1.5 * r1 && H(kk) - L(kk) > 0 && Math.abs(C(kk) - O(kk)) >= 0.9 * (H(kk) - L(kk))) anyLegoutTRok = true;
    }

    // ② merged base block
    const bO = O(i2e);
    const bC = C(i2);
    let bH = H(i2);
    let bL = L(i2);
    let boringFails = 0;
    let wickFails = 0;
    for (let j = i2; j <= i2e; j++) {
      bH = Math.max(bH, H(j));
      bL = Math.min(bL, L(j));
      boringFails += ATR(j) > H(j) - L(j) ? 0 : 1;
      const rj = H(j) - L(j);
      const uwj = H(j) - Math.max(O(j), C(j));
      const dwj = Math.min(O(j), C(j)) - L(j);
      wickFails += rj > 0 && uwj >= (wickPct / 100) * rj && dwj >= (wickPct / 100) * rj ? 0 : 1;
    }
    const allBoring = boringFails === 0;
    let bodied = 0;
    for (let j = i2; j <= i2e; j++) if (Math.abs(C(j) - O(j)) > this.nbMax + 1e-6) bodied++;
    const nb_ok = !this.v214 || this.nbMax < 0 || bodied > 0;
    let swFails = 0;
    if (this.sw) for (let j = i2; j <= i2e; j++) {
      const rj = H(j) - L(j);
      if (TR(i1) > 0 && TR(j) >= 0.85 * TR(i1) && (rj <= 0 || Math.abs(C(j) - O(j)) > 0.5 * rj)) swFails++;
    }
    const sw_ok = swFails === 0;
    let mbFails = 0;
    if (this.minBB > 0) for (let j = i2; j <= i2e; j++) if (Math.abs(C(j) - O(j)) < this.minBB - 1e-6) mbFails++;
    const w_ok = wickFails === 0;

    const r2 = bH - bL;
    const bd2 = Math.abs(bC - bO);
    const bTop = Math.max(bO, bC);
    const bBot = Math.min(bO, bC);

    // merged ③
    const o3 = O(n - 1);
    const c3 = C(0);
    const h3 = n === 1 ? H(0) : n === 2 ? Math.max(H(0), H(1)) : Math.max(H(0), Math.max(H(1), H(2)));
    const l3 = n === 1 ? L(0) : n === 2 ? Math.min(L(0), L(1)) : Math.min(L(0), Math.min(L(1), L(2)));
    const r3 = h3 - l3;
    const bd3 = Math.abs(c3 - o3);

    // Rule K: any big leg-out (range > ATR) must have a real body (≥ legBigBodyPct% of range)
    // Rule LW: at most maxWickyLegouts leg-outs may have a single wick larger than its body
    let legBodyFail = false;
    let wickyLegouts = 0;
    for (let kk = 0; kk <= n - 1; kk++) {
      const rk = H(kk) - L(kk);
      const bk = Math.abs(C(kk) - O(kk));
      if (rk > ATR(kk) && (rk <= 0 || (bk / rk) * 100 < legBigBodyPct)) legBodyFail = true;
      const uwk = H(kk) - Math.max(O(kk), C(kk));
      const dwk = Math.min(O(kk), C(kk)) - L(kk);
      if (Math.max(uwk, dwk) > bk) wickyLegouts += 1;
    }
    const k_ok = !legBodyFail;
    const lw_ok = wickyLegouts <= maxWickyLegouts;

    // merge validity
    const leg3Green = C(n - 1) > O(n - 1);
    const leg3Red = C(n - 1) < O(n - 1);
    const leg3Dir = leg3Green || leg3Red;
    const sameCol4 = n >= 2 ? (leg3Green ? C(n - 2) > O(n - 2) : C(n - 2) < O(n - 2)) : true;
    const bd4ok = n >= 2 ? (H(n - 2) - L(n - 2) > 0 ? (Math.abs(C(n - 2) - O(n - 2)) / (H(n - 2) - L(n - 2))) * 100 > 40 : false) : true;
    const sameCol5 = n >= 3 ? (leg3Green ? C(0) > O(0) : C(0) < O(0)) : true;
    const bd5ok = n >= 3 ? (H(0) - L(0) > 0 ? (Math.abs(C(0) - O(0)) / (H(0) - L(0))) * 100 > 35 : false) : true;
    const bdFirstOk =
      H(n - 1) - L(n - 1) > 0
        ? (Math.abs(C(n - 1) - O(n - 1)) / (H(n - 1) - L(n - 1))) * 100 > vBodyPct || ATB(n - 1) * 0.7 < Math.abs(C(n - 1) - O(n - 1))
        : false;
    const bdCombo = n >= 3 ? bd4ok || bd5ok : n === 2 ? bd4ok && bd5ok : true;
    const mergeOk = leg3Dir && bdFirstOk && sameCol4 && sameCol5 && bdCombo;

    // B rules
    const b1 = r1 > ATR(i1) * 0.9 || bd1 > ATB(i1) || (this.v214 && r1 >= 1.7 * r2 && bd1 >= 0.75 * r1);
    const b2 = allBoring;
    const b3 = H(n - 1) - L(n - 1) > ATR(n - 1) * 0.8;
    const c = bd1 >= bd2;

    // size rules
    const j_bigBody = bd3 >= this.legoutMul * r2;
    const r_bigger = r1 <= r3;
    const s_bigger = bd3 > bd1;
    const t_notHuge = bd3 <= tMaxRatio * bd1;

    // demand / supply
    const d_gap = Math.abs(bTop - h3) >= dBodyMul * bd1;
    const d_breakout = c3 > bH;
    const s_gap = Math.abs(bBot - l3) >= dBodyMul * bd1;
    const s_breakdown = c3 < bL;
    const q_dbr = c3 > H(i1);
    const q_rbd = c3 < L(i1);

    // Rule U
    const u_dn = o3 >= (bL + Math.min(bTop, bBot)) / 2 && l3 >= bL;
    const u_up = o3 <= (bH + Math.max(bTop, bBot)) / 2 && h3 <= bH;

    // Rule V
    const v_clean = r3 > 0 ? (bd3 / r3) * 100 > vBodyPct : false;

    // Rule X
    const bodyBot1 = Math.min(O(i1), C(i1));
    const xLevelUp = bodyBot1 + (xPct / 100) * bd1;
    const xLevelDn = bodyBot1 + (1 - xPct / 100) * bd1;
    const x_up = bL >= xLevelUp;
    const x_dn = bH <= xLevelDn;

    // Rule Y / Z
    const y_solid = r1 > 0 ? (bd1 / r1) * 100 >= vBodyPct : false;
    const wick1_total = r1 - bd1;
    const z_solid = bd1 > wick1_total;

    // Rule LD: leg-in extreme → leg-out extreme must exceed ATR × ldMul
    const ldMul = 1.0;
    const liLoDist_dbr = h3 - O(i1) > ldMul * ATR(i1);
    const liLoDist_rbd = L(i1) - l3 > ldMul * ATR(i1);
    const liLoDist_rbr = h3 - bL > ldMul * ATR(i1);
    const liLoDist_dbd = bH - l3 > ldMul * ATR(i1);

    // Rule LO: leg-out staircase (each later leg-out must not cross back over the previous)
    const isDemand = ptype === "DBR" || ptype === "RBR";
    let legoutStair = true;
    if (n >= 2) {
      for (let kk = n - 2; kk >= 0; kk--) {
        const crossed = isDemand ? H(kk) < H(kk + 1) : L(kk) > L(kk + 1);
        if (crossed) legoutStair = false;
      }
    }

    // v2.14 rules
    const ltNeed = ((n === 1 ? 33 : n === 2 ? 40 : 45) / 100) * 3 * r2;
    const lt_ok = !this.v214 || (r2 > 0 && (isDemand ? h3 - bH : bL - l3) >= ltNeed);
    const lb_ok = !this.v214 || (isDemand ? l3 >= bL : h3 <= bH);
    const fl_ok = !this.v214 || ptype === "DBR" || ptype === "RBD" || (ptype === "RBR" ? C(n - 1) > bH : C(n - 1) < bL);
    const v214ok = nb_ok && lt_ok && lb_ok && fl_ok && sw_ok && (!this.bc || b <= n) && mbFails === 0;

    let ok = false;
    if (ptype === "DBR") {
      const a = C(i1) < O(i1) && c3 > o3;
      const o = o3 <= O(i1);
      ok =
        a && b1 && b2 && b3 && c && d_gap && o && d_breakout && j_bigBody && r_bigger && s_bigger && t_notHuge && v_clean && mergeOk && x_dn && z_solid && q_dbr && u_dn && y_solid && anyLegoutTRok && legoutStair && liLoDist_dbr && k_ok && lw_ok;
    } else if (ptype === "RBR") {
      const a = C(i1) > O(i1) && c3 > o3;
      const ff = o3 <= bTop + bd1 * 2;
      const gg = o3 >= bL;
      ok =
        a && b1 && b2 && b3 && c && d_gap && ff && gg && d_breakout && j_bigBody && r_bigger && s_bigger && t_notHuge && v_clean && mergeOk && x_up && y_solid && anyLegoutTRok && legoutStair && liLoDist_rbr && k_ok && lw_ok;
    } else if (ptype === "RBD") {
      const a = C(i1) > O(i1) && c3 < o3;
      const o = o3 >= O(i1);
      ok =
        a && b1 && b2 && b3 && c && s_gap && o && s_breakdown && j_bigBody && r_bigger && s_bigger && t_notHuge && v_clean && mergeOk && x_up && z_solid && q_rbd && u_up && y_solid && anyLegoutTRok && legoutStair && liLoDist_rbd && k_ok && lw_ok;
    } else {
      const a = C(i1) < O(i1) && c3 < o3;
      const ff = o3 >= bBot - bd1 * 2;
      const gg = o3 <= bH;
      ok =
        a && b1 && b2 && b3 && c && s_gap && ff && gg && s_breakdown && j_bigBody && r_bigger && s_bigger && t_notHuge && v_clean && mergeOk && x_dn && y_solid && anyLegoutTRok && legoutStair && liLoDist_dbd && k_ok && lw_ok;
    }
    return { ok: ok && v214ok, w: w_ok, e: e_ok };
  }

  // search order: fewest boring first, then shortest leg-out (first pass wins).
  private findType(k: number, ptype: "DBR" | "RBR" | "RBD" | "DBD"): ZoneType | null {
    for (let b = 1; b <= this.maxB; b++) {
      for (let n = this.minLO; n <= maxLegOut; n++) {
        const r = this.evalPattern(k, n, b, ptype);
        if (r && r.ok) return { name: ptype, n, b, w: r.w, e: r.e };
      }
    }
    return null;
  }

  // Pine v2.15 ENTRY FILTERS: follow the zone of type t formed at bar k until price touches its entry (boring high
  // for demand, boring low for supply). Touch → "✓ entry" / "✗ fast" (≤ 4 candles) / "✗ straight" (no leg-out-colour
  // candle closed since the zone formed). Near miss (within 10% of the formation ATR, no touch) uses the zone up and
  // is labelled only if it would have been fast / straight. Uses no candle after the event, so it is replay-safe.
  entryEvent(k: number, t: ZoneType): EntryEvent | null {
    const key = k * 4 + ["DBR", "RBR", "RBD", "DBD"].indexOf(t.name);
    const cached = this.evCache.get(key);
    if (cached !== undefined) return cached;
    const d = this.d;
    const dem = t.name === "DBR" || t.name === "RBR";
    let en = dem ? -Infinity : Infinity;
    for (let j = k - t.n - t.b + 1; j <= k - t.n; j++) en = dem ? Math.max(en, d.highAt(j)) : Math.min(en, d.lowAt(j));
    const near = (NEAR_PCT / 100) * d.atrAt(k);
    let away = false;
    let out: EntryEvent | null = null;
    for (let j = k + 1; ; j++) {
      const age = j - k;
      if (age >= MAX_WAIT) break;
      const h = d.highAt(j), l = d.lowAt(j), o = d.openAt(j), c = d.closeAt(j);
      if (j >= d.barCount || !isFinite(h) || !isFinite(l)) return null;   // ran out of loaded data: don't cache
      const fast = age <= FAST_BARS;
      if (dem ? l <= en : h >= en) {
        out = { bar: j, demand: dem, text: fast ? "✗ fast" : !away ? "✗ straight" : "✓ entry" };
        break;
      }
      if ((dem ? l - en : en - h) <= near) {
        if (fast || !away) out = { bar: j, demand: dem, text: fast ? "✗ fast" : "✗ straight" };
        break;
      }
      if (dem ? c > o : c < o) away = true;
    }
    if (this.evCache.size > 300000) this.evCache.clear();
    this.evCache.set(key, out);
    return out;
  }

  at(k: number): ZoneResult | null {
    const cached = this.resCache.get(k);
    if (cached !== undefined) return cached;

    const dbr = this.findType(k, "DBR");
    const rbr = this.findType(k, "RBR");
    const rbd = this.findType(k, "RBD");
    const dbd = this.findType(k, "DBD");

    const types = [dbr, rbr, rbd, dbd].filter((t): t is ZoneType => t !== null);
    let result: ZoneResult | null = null;
    if (types.length) {
      const color = dbr ? COL_DBR : rbr ? COL_RBR : rbd ? COL_RBD : COL_DBD;
      const maxN = Math.max(dbr?.n ?? 0, rbr?.n ?? 0, rbd?.n ?? 0, dbd?.n ?? 0);
      const maxB = Math.max(dbr?.b ?? 0, rbr?.b ?? 0, rbd?.b ?? 0, dbd?.b ?? 0);
      const span = maxN + maxB + (maxN > 0 ? 1 : 0);
      result = { span, color, types };
    }
    if (this.resCache.size > 300000) this.resCache.clear();
    this.resCache.set(k, result);
    return result;
  }
}

// star tag identical to Pine tag() (labels carry the "Old " prefix from the script)
export function zoneTag(t: ZoneType): string {
  return "Old " + t.name + " b" + t.b + (t.n > 1 ? " L" + t.n : "") + (t.w ? " ★" : "") + (t.e ? "★" : "") + (t.n >= 3 ? "★" : "");
}
